import {useMemo, useState} from 'react';
import useStripeConnect from '../../../../../common/useStripeConnect';
import {
  ConnectAccountManagement,
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
  ConnectNotificationBanner,
  ConnectPayouts,
} from '@stripe/react-connect-js';
import {
  fetchRequest,
  securedFetchRequest,
} from '@bidmii/common/lib/util/FetchRequest';
import * as Sentry from '@sentry/react';
import {API_URL} from '../../../../../constants';
import {useRecoilState} from 'recoil';
import {userState} from '../../../../../recoil/user/userAtom';
import PrimaryButton from '../../../../../common/StyledComponents/PrimaryButton';
import {Typography} from '@mui/material';
import useSnackbar from '../../../../../hooks/useSnackbar';

export default function StripeContractorConnectAccount() {
  const {openSnackbar} = useSnackbar();
  const [user, setUser] = useRecoilState(userState);
  const [accountCreatePending, setAccountCreatePending] = useState(false);
  const [onboardingExited, setOnboardingExited] = useState(false);
  const [connectedAccountId, setConnectedAccountId] = useState(
    user.stripeConnectAccountId,
  );
  const stripeConnectInstance = useStripeConnect(connectedAccountId);
  console.log('stripeConnectInstance :>> ', stripeConnectInstance);

  const requestFunction =
    process.env.REACT_APP_FORCE_WEB_VIEW === '1' ||
    (window as $TSFixMe).ReactNativeWebView
      ? fetchRequest
      : securedFetchRequest;
  const options = useMemo(() => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    };
    if (
      process.env.REACT_APP_FORCE_WEB_VIEW === '1' ||
      (window as $TSFixMe).ReactNativeWebView
    ) {
      // @ts-expect-error
      requestOptions.headers['Firebase-Authorization'] = sessionStorage.getItem(
        'Firebase-Authorization',
      );
    }
    return requestOptions;
  }, []);

  const createConnectAccount = async () => {
    console.log('Creating Connect Account...');
    setAccountCreatePending(true);

    requestFunction(`${API_URL}/user-account`, options)
      .then((res) => res.json())
      .then((json) => {
        setAccountCreatePending(false);
        const {account, error} = json;

        if (account) {
          setConnectedAccountId(account);
          setUser({...user, stripeConnectAccountId: account});
        }

        if (error) {
          openSnackbar(error, 'error');
        }
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'MobileProfile.tsx toggleSaveContractor()');
          Sentry.captureException(err);
        });
      });
  };

  const handleClickManagePaymentMethods = async () => {
    requestFunction(`${API_URL}/user-customer-account-session`, options)
      .then((res) => res.text())
      .then((text) => {
        window.location.href = text;
      });
  };

  if (!connectedAccountId) {
    return (
      <div className="container">
        <div className="content">
          <Typography variant="h6" gutterBottom>
            Configure your payments account:
          </Typography>
          <PrimaryButton
            onClick={createConnectAccount}
            disabled={accountCreatePending}>
            CONFIGURE
          </PrimaryButton>
          {stripeConnectInstance && (
            <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
              <ConnectAccountOnboarding
                onExit={() => setOnboardingExited(true)}
              />
            </ConnectComponentsProvider>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="content">
        {stripeConnectInstance && (
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <ConnectNotificationBanner />
            <ConnectAccountManagement />
            <ConnectPayouts />
          </ConnectComponentsProvider>
        )}

        {(accountCreatePending || onboardingExited) && (
          <div className="dev-callout">
            {connectedAccountId && (
              <>
                <p>
                  Your connected account ID is:{' '}
                  <code className="bold">{connectedAccountId}</code>
                </p>
                <PrimaryButton onClick={handleClickManagePaymentMethods}>
                  MANAGE
                </PrimaryButton>
              </>
            )}
            {accountCreatePending && <p>Creating a connected account...</p>}
            {onboardingExited && (
              <p>The Account Onboarding component has exited</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
